import React, { useState, useCallback, useEffect, useMemo } from "react";
import { FaMapMarkerAlt, FaBox, FaTruck, FaUser } from "react-icons/fa";
import {
  IoSearchOutline,
  IoLocationOutline,
  IoTimeOutline,
  IoAlertCircle,
  IoCheckmarkCircle,
} from "react-icons/io5";
import { useParams, useNavigate } from "react-router-dom";
import {
  GoogleMap,
  Marker,
  DirectionsService,
  DirectionsRenderer,
  useLoadScript,
  Polyline,
  InfoWindow,
} from "@react-google-maps/api";
import Api from "../axiosConfig";

// Google Maps libraries
const libraries = ["places"];

// Function to get coordinates from address using Geocoding API
const getCoordinates = async (address) => {
  if (!address) return null;
  
  // If address is already coordinates object, return it
  if (typeof address === 'object' && address.lat && address.lng) {
    return address;
  }

  // If address is not a string (e.g., null, undefined, or other types), return null
  if (typeof address !== 'string') {
    console.warn('Invalid address format:', address);
    return null;
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
        address
      )}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
    );
    const data = await response.json();

    if (data.status === 'ZERO_RESULTS') {
      console.warn('No results found for address:', address);
      return null;
    }

    if (data.status !== 'OK') {
      console.error('Geocoding API error:', data.status, data.error_message);
      return null;
    }

    if (data.results && data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry.location;
      return { lat, lng };
    }

    return null;
  } catch (error) {
    console.error("Error geocoding address:", error);
    return null;
  }
};

// Function to format date with timezone
const formatDate = (dateString, includeDate = true) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    if (includeDate) {
      options.year = 'numeric';
      options.month = 'short';
      options.day = 'numeric';
    }

    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "0.5rem",
};

const options = {
  styles: [
    {
      featureType: "all",
      elementType: "geometry.fill",
      stylers: [{ weight: "2.00" }],
    },
    {
      featureType: "all",
      elementType: "geometry.stroke",
      stylers: [{ color: "#9c9c9c" }],
    },
    {
      featureType: "all",
      elementType: "labels.text",
      stylers: [{ visibility: "on" }],
    },
  ],
  disableDefaultUI: true,
  zoomControl: true,
};

const getStatusColor = (status) => {
  switch (status) {
    case "delivered":
      return "bg-green-500";
    case "in_transit":
      return "bg-blue-500";
    case "pending":
      return "bg-yellow-500";
    default:
      return "bg-gray-500";
  }
};

const calculateCurrentLocation = (timeline) => {
  const now = new Date();

  // Find the two timeline points that bracket the current time
  for (let i = 0; i < timeline.length - 1; i++) {
    const currentPoint = new Date(timeline[i].date);
    const nextPoint = new Date(timeline[i + 1].date);

    if (now >= nextPoint && now <= currentPoint) {
      // Calculate progress between these two points
      const totalTime = currentPoint - nextPoint;
      const elapsedTime = now - nextPoint;
      const progress = elapsedTime / totalTime;

      // Interpolate between the two points
      const lat =
        timeline[i + 1].coords.lat +
        (timeline[i].coords.lat - timeline[i + 1].coords.lat) * progress;
      const lng =
        timeline[i + 1].coords.lng +
        (timeline[i].coords.lng - timeline[i + 1].coords.lng) * progress;

      return { lat, lng };
    }
  }

  // If we're before the first point or after the last point, return the appropriate endpoint
  if (now < new Date(timeline[timeline.length - 1].date)) {
    return timeline[timeline.length - 1].coords; // Origin
  }
  return timeline[0].coords; // Destination
};

const Tracking = () => {
  const { trackingId: urlTrackingId } = useParams();
  const navigate = useNavigate();

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [trackingNumber, setTrackingNumber] = useState(urlTrackingId || "");
  const [trackingResult, setTrackingResult] = useState(null);
  const [error, setError] = useState("");
  const [directions, setDirections] = useState(null);
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);

  const currentLocationIcon = useMemo(() => {
    if (!isLoaded) return null;

    return {
      url:
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#3B82F6" width="32px" height="32px">
          <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" />
        </svg>
      `),
      anchor: new window.google.maps.Point(16, 16),
      scaledSize: new window.google.maps.Size(32, 32),
    };
  }, [isLoaded]);

  useEffect(() => {
    if (urlTrackingId) {
      setTrackingNumber(urlTrackingId);
      handleTracking(urlTrackingId);
      // Update URL to /tracking to avoid issues with refresh
      navigate("/tracking", { replace: true });
    }
  }, [urlTrackingId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    handleTracking(trackingNumber);
  };

  const handleTracking = async (id) => {
    if (!id) {
      setError("Please enter a tracking number");
      return;
    }

    setError("");
    setTrackingResult(null);
    setLoading(true);

    try {
      const response = await Api.get(`/v1/deliveries/track/${id}`, {
        headers: {
          'X-Timezone-Offset': new Date().getTimezoneOffset()
        }
      });

      if (response.data.success && response.data.data) {
        const originCoords = await getCoordinates(response.data.data.origin);
        const destinationCoords = await getCoordinates(response.data.data.destination);

        // Transform the API response to match our UI format
        const transformedData = {
          status: response.data.data.status.toLowerCase(),
          estimatedDelivery: formatDate(response.data.data.estimatedDelivery),
          origin: response.data.data.origin,
          destination: response.data.data.destination,
          originCoords: originCoords || null,
          destinationCoords: destinationCoords || null,
          currentLocation: response.data.data.currentLocation,
          currentLocationCoords: response.data.data.currentLocationCoords || null,
          timeline: await Promise.all(
            response.data.data.timeline.map(async (item) => {
              const location = item.location || "";
              const isToday = new Date(item.timestamp).toDateString() === new Date().toDateString();
              
              let locationText;
              let coords;

              if (typeof location === 'object') {
                if (location.lat && location.lng) {
                  locationText = `Location (${location.lat.toFixed(4)}, ${location.lng.toFixed(4)})`;
                  coords = location;
                } else {
                  locationText = location.formatted || location.address || 'Location unavailable';
                  coords = await getCoordinates(locationText);
                }
              } else {
                locationText = location || 'Location unavailable';
                coords = await getCoordinates(locationText);
              }
              
              return {
                status: item.status.toLowerCase(),
                location: locationText,
                date: formatDate(item.timestamp, !isToday),
                completed: true,
                coords: coords
              };
            })
          ),
        };

        // Calculate current location based on the most recent timeline entry
        const sortedTimeline = [...transformedData.timeline].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        transformedData.currentLocationCoords =
          sortedTimeline[0]?.coords || transformedData.originCoords;

        // Only set the tracking result if we have valid coordinates
        if (
          transformedData.originCoords &&
          transformedData.destinationCoords
        ) {
          setTrackingResult(transformedData);
        } else {
          setError("Unable to get location coordinates. Please try again.");
        }
      } else {
        setError("Invalid tracking number");
      }
    } catch (error) {
      console.error("Error tracking delivery:", error);
      setError(error.response?.data?.message || "Error tracking delivery");
    } finally {
      setLoading(false);
    }
  };

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  useEffect(() => {
    if (isLoaded && trackingResult) {
      // Set map center to origin coordinates when tracking result changes
      if (map && trackingResult.originCoords) {
        map.panTo(trackingResult.originCoords);
        map.setZoom(12);
      }
    }
  }, [isLoaded, trackingResult, map]);

  if (loadError) {
    console.error("Error loading maps:", loadError);
    return <div className="text-red-500">Error loading Google Maps</div>;
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-dark"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex flex-col px-5 justify-center bg-[url('/img/Website/IMPACTCars.png')] bg-cover py-12 sm:px-6 lg:px-8">
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Content */}
      <div className="relative">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="text-center text-3xl font-bold tracking-tight text-white">
            Track Your Package
          </h2>
        </div>

        <div className="mt-8 mx-auto w-full lg:max-w-5xl">
          <div className="bg-white max-w-3xl mx-auto py-8 px-4 shadow-lg rounded-lg sm:px-10">
            {/* Tracking Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleTracking(trackingNumber);
              }}
              className="space-y-6 mb-8"
            >
              <div>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IoSearchOutline
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    className="block w-full pl-10 py-3 sm:text-sm border-gray-400 border-[1px] border-solid rounded-md focus:outline-none"
                    placeholder="Enter tracking number"
                  />
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-dark hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Track Package
                </button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600" id="email-error">
                  {error}
                </p>
              )}
            </form>

            {/* Tracking Result */}
            {trackingResult && (
              <div className="space-y-6">
                {/* Status Overview */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <IoLocationOutline className="h-6 w-6 text-primary" />
                      <div>
                        <p className="text-sm text-gray-500">
                          Current Location
                        </p>
                        <p className="font-medium">
                          {trackingResult.currentLocation || 'Not available'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <IoTimeOutline className="h-6 w-6 text-primary" />
                      <div>
                        <p className="text-sm text-gray-500">
                          Estimated Delivery
                        </p>
                        <p className="font-medium">
                          {trackingResult.estimatedDelivery || 'Not available'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FaBox className="h-6 w-6 text-primary" />
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <p
                          className={`inline-flex items-center text-white px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
                            trackingResult.status
                          )}`}
                        >
                          {trackingResult.status.replace("_", " ")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline and Map */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Timeline */}
                  <div className="space-y-4">
                    <h3 className="font-medium">Status History</h3>
                    <div className="flow-root">
                      <ul className="-mb-8">
                        {trackingResult.timeline.map((event, index) => (
                          <li key={index}>
                            <div className="relative pb-8">
                              {index < trackingResult.timeline.length - 1 && (
                                <span
                                  className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                                  aria-hidden="true"
                                />
                              )}
                              <div className="relative flex space-x-3">
                                <div>
                                  <span
                                    className={`h-8 w-8 rounded-full flex items-center justify-center ${getStatusColor(
                                      event.status
                                    )}`}
                                  >
                                    {event.status === "delivered" && (
                                      <IoCheckmarkCircle className="text-2xl text-white" />
                                    )}
                                    {event.status === "in_transit" && (
                                      <FaTruck className="text-lg text-white" />
                                    )}
                                    {event.status === "picked_up" && (
                                      <FaBox className="text-lg text-white" />
                                    )}
                                    {event.status === "approved" && (
                                      <IoCheckmarkCircle className="text-2xl text-white" />
                                    )}
                                    {event.status === "pending" && (
                                      <IoAlertCircle className="text-2xl text-white" />
                                    )}
                                  </span>
                                </div>
                                <div className="flex-1">
                                  <div className="flex justify-between">
                                    <p className="font-medium text-sm capitalize">
                                      {event.status.replace("_", " ")}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {event.date}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Map */}
                  <div className="flex-1 min-h-[400px]">
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={trackingResult.currentLocationCoords}
                      zoom={10}
                      options={options}
                      onLoad={onLoad}
                    >
                      {/* Origin Marker */}
                      <Marker
                        position={trackingResult.originCoords}
                        onClick={() => setSelectedMarker("origin")}
                      >
                        {selectedMarker === "origin" && (
                          <InfoWindow
                            position={trackingResult.originCoords}
                            onCloseClick={() => setSelectedMarker(null)}
                          >
                            <div>
                              <p className="font-semibold">Origin</p>
                              <p>{trackingResult.origin}</p>
                            </div>
                          </InfoWindow>
                        )}
                      </Marker>

                      {/* Destination Marker */}
                      <Marker
                        position={trackingResult.destinationCoords}
                        onClick={() => setSelectedMarker("destination")}
                      >
                        {selectedMarker === "destination" && (
                          <InfoWindow
                            position={trackingResult.destinationCoords}
                            onCloseClick={() => setSelectedMarker(null)}
                          >
                            <div>
                              <p className="font-semibold">Destination</p>
                              <p>{trackingResult.destination}</p>
                            </div>
                          </InfoWindow>
                        )}
                      </Marker>

                      {/* Current Location Marker */}
                      {currentLocationIcon && (
                        <Marker
                          position={trackingResult.currentLocationCoords}
                          onClick={() => setSelectedMarker("current")}
                          icon={currentLocationIcon}
                          label={{
                            text: "Current",
                            color: "#2563EB",
                            fontSize: "12px",
                            fontWeight: "bold",
                            className: "marker-label",
                          }}
                        >
                          {selectedMarker === "current" && (
                            <InfoWindow
                              position={trackingResult.currentLocationCoords}
                              onCloseClick={() => setSelectedMarker(null)}
                            >
                              <div>
                                <p className="font-semibold">
                                  Current Location
                                </p>
                                <p>{trackingResult.currentLocation}</p>
                                <p
                                  className={`text-sm ${getStatusColor(
                                    trackingResult.status
                                  ).replace("bg-", "text-")}`}
                                >
                                  {trackingResult.status.replace("_", " ")}
                                </p>
                              </div>
                            </InfoWindow>
                          )}
                        </Marker>
                      )}

                      {/* Route Line */}
                      {trackingResult.originCoords &&
                        trackingResult.currentLocationCoords &&
                        trackingResult.destinationCoords && (
                          <Polyline
                            path={[
                              trackingResult.originCoords,
                              trackingResult.currentLocationCoords,
                              trackingResult.destinationCoords,
                            ]}
                            options={{
                              strokeColor: "#1a365d",
                              strokeOpacity: 0.8,
                              strokeWeight: 3,
                              geodesic: true,
                            }}
                          />
                        )}
                    </GoogleMap>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tracking;
